import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { getLunarMonthDays, getLunarLeapMonth, getLunarLeapMonthDays, lunarToSolar } from '../../../utils/lunarCalc';
import FourPillarsTab, { FourPillarsValue } from './FourPillarsTab';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { reverseFourPillarsToSolar, solarToFourPillarsValue } from '../../../utils/calcTools/bazi/fourPillarsReverse';
import { hasRealBirthHour } from '@/utils/birthTime';

interface BirthTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  title?: string;
}

const HEAVENLY_STEMS = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
const EARTHLY_BRANCHES = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
const FOUR_PILLARS_HOURS_BASE = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];

function getSexagenary(year: number) {
  const stemIdx = (year - 4) % 10;
  const branchIdx = (year - 4) % 12;
  return HEAVENLY_STEMS[(stemIdx + 10) % 10] + EARTHLY_BRANCHES[(branchIdx + 12) % 12];
}

interface WheelColumnProps {
  items: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  width?: string;
  colKey?: string;
}

function WheelColumn({ items, selectedIndex, onSelect, width = 'flex-1', colKey }: WheelColumnProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const ITEM_HEIGHT = 44;
  const VISIBLE = 5;
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startScrollTop = useRef(0);
  const animating = useRef(false);
  const prevColKey = useRef<string | undefined>(colKey);

  // When colKey changes (items list changed), re-scroll to the (clamped) selectedIndex
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(selectedIndex, items.length - 1));
    el.scrollTop = clamped * ITEM_HEIGHT;
    if (clamped !== selectedIndex) onSelect(clamped);
    prevColKey.current = colKey;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colKey, items.length]);

  // Normal scroll to selectedIndex when it changes externally
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (colKey !== prevColKey.current) return; // already handled above
    el.scrollTop = selectedIndex * ITEM_HEIGHT;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  const snapToNearest = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(idx, items.length - 1));
    const target = clamped * ITEM_HEIGHT;
    if (el.scrollTop !== target) {
      animating.current = true;
      el.scrollTo({ top: target, behavior: 'smooth' });
      setTimeout(() => { animating.current = false; }, 300);
    }
    if (clamped !== selectedIndex) onSelect(clamped);
  }, [items.length, selectedIndex, onSelect]);

  const handleScroll = useCallback(() => {
    if (isDragging.current || animating.current) return;
    snapToNearest();
  }, [snapToNearest]);

  const onTouchStart = (e: React.TouchEvent) => {
    isDragging.current = true;
    startY.current = e.touches[0].clientY;
    startScrollTop.current = containerRef.current?.scrollTop ?? 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    containerRef.current.scrollTop = startScrollTop.current + (startY.current - e.touches[0].clientY);
  };
  const onTouchEnd = () => {
    isDragging.current = false;
    snapToNearest();
  };

  const onMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startY.current = e.clientY;
    startScrollTop.current = containerRef.current?.scrollTop ?? 0;
    e.preventDefault();
  };
  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    containerRef.current.scrollTop = startScrollTop.current + (startY.current - e.clientY);
  }, []);
  const onMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    snapToNearest();
  }, [snapToNearest]);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const containerHeight = ITEM_HEIGHT * VISIBLE;
  const paddingItems = Math.floor(VISIBLE / 2);

  return (
    <div className={`${width} relative overflow-hidden`} style={{ height: containerHeight }}>
      <div
        className="absolute left-0 right-0 pointer-events-none z-10 rounded-xl"
        style={{
          top: paddingItems * ITEM_HEIGHT,
          height: ITEM_HEIGHT,
          background: 'var(--accent-primary-15)',
          border: '1px solid var(--accent-primary-30)',
        }}
      />
      <div
        className="absolute inset-x-0 top-0 pointer-events-none z-20"
        style={{ height: paddingItems * ITEM_HEIGHT, background: 'linear-gradient(to bottom, var(--bg-primary-96), transparent)' }}
      />
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none z-20"
        style={{ height: paddingItems * ITEM_HEIGHT, background: 'linear-gradient(to top, var(--bg-primary-96), transparent)' }}
      />
      <div
        ref={containerRef}
        className="overflow-y-scroll"
        style={{ height: containerHeight, scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onScroll={handleScroll}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
      >
        {Array.from({ length: paddingItems }).map((_, i) => (
          <div key={`top-${i}`} style={{ height: ITEM_HEIGHT }} />
        ))}
        {items.map((item, idx) => {
          const dist = Math.abs(idx - selectedIndex);
          const opacity = dist === 0 ? 1 : dist === 1 ? 0.55 : 0.25;
          const scale = dist === 0 ? 1 : dist === 1 ? 0.88 : 0.78;
          const color = dist === 0 ? 'var(--text-primary)' : 'var(--text-secondary)';
          const fontWeight = dist === 0 ? '700' : '400';
          return (
            <div
              key={idx}
              onClick={() => onSelect(idx)}
              className="flex items-center justify-center cursor-pointer select-none"
              style={{
                height: ITEM_HEIGHT,
                opacity,
                transform: `scale(${scale})`,
                transition: 'opacity 0.2s, transform 0.2s',
                color,
                fontWeight,
                fontSize: dist === 0 ? '1.05rem' : '0.85rem',
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              {item}
            </div>
          );
        })}
        {Array.from({ length: paddingItems }).map((_, i) => (
          <div key={`bot-${i}`} style={{ height: ITEM_HEIGHT }} />
        ))}
      </div>
    </div>
  );
}

type TabType = 'solar' | 'lunar' | 'fourPillars';

function MobileBirthTimePicker({ value, onChange, onClose, title }: BirthTimePickerProps) {
  const { t } = useTranslation('profileManage');
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<TabType>('solar');

  // ── i18n arrays ──
  const lunarMonthNames = t('birthTimePicker.lunarMonths', { returnObjects: true }) as string[];
  const lunarDayNames = t('birthTimePicker.lunarDays', { returnObjects: true }) as string[];
  const leapPrefix = t('birthTimePicker.leapPrefix');
  const lunarYearUnit = t('birthTimePicker.lunarYearUnit');
  const lunarHourUnit = t('birthTimePicker.lunarHourUnit');
  const unknownLabel = t('birthTimePicker.unknown');

  const parseInitial = () => {
    // 同时支持空格和 'T' 两种分隔符
    const normalized = value.includes('T') ? value.replace('T', ' ') : value;
    const parts = normalized.split(' ');
    const dateParts = (parts[0] || '1990-01-01').split('-');
    const timeParts = (parts[1] || '00:00').split(':');
    return {
      year: parseInt(dateParts[0]) || 1990,
      month: parseInt(dateParts[1]) || 1,
      day: parseInt(dateParts[2]) || 1,
      hour: parseInt(timeParts[0]) || 0,
      minute: parseInt(timeParts[1]) || 0,
    };
  };

  const init = parseInitial();
  const YEAR_START = 1900;
  const YEAR_END = 2100;

  // ── 公历 ──
  const solarYears = Array.from({ length: YEAR_END - YEAR_START + 1 }, (_, i) => String(YEAR_START + i));
  const solarMonths = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

  const [solarYearIdx, setSolarYearIdx] = useState(init.year - YEAR_START);
  const [solarMonthIdx, setSolarMonthIdx] = useState(init.month - 1);
  const [solarDayIdx, setSolarDayIdx] = useState(init.day - 1);
  const [solarHourIdx, setSolarHourIdx] = useState(() => {
    // 无真实小时（空值/档案占位）时默认 12点（索引 12+1=13）
    if (!hasRealBirthHour(value)) return 13;
    const h = init.hour;
    // solarHours = [unknownLabel, '00', '01', ..., '23']
    if (h >= 0 && h <= 23) return h + 1;
    return 0;
  });
  const [solarMinIdx, setSolarMinIdx] = useState(() => {
    const m = init.minute;
    // solarMins = [unknownLabel, '00', '01', ..., '59']
    if (m >= 0 && m <= 59) return m + 1;
    return 0;
  });

  const solarYear = YEAR_START + solarYearIdx;
  const solarMonth = solarMonthIdx + 1;

  // Dynamic solar days based on year & month
  const solarDayCount = new Date(solarYear, solarMonth, 0).getDate();
  const solarDays = Array.from({ length: solarDayCount }, (_, i) => String(i + 1).padStart(2, '0'));
  const solarDayColKey = `solar-day-${solarYear}-${solarMonth}`;

  // Clamp solar day when month/year changes
  useEffect(() => {
    if (solarDayIdx >= solarDayCount) {
      setSolarDayIdx(solarDayCount - 1);
    }
  }, [solarYear, solarMonth, solarDayCount]);

  const solarHours = [unknownLabel, ...Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))];
  const solarMins  = [unknownLabel, ...Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))];

  // ── 农历 ──
  const [lunarYearIdx, setLunarYearIdx] = useState(init.year - YEAR_START);
  const [lunarMonthIdx, setLunarMonthIdx] = useState(init.month - 1);
  const [lunarDayIdx, setLunarDayIdx] = useState(Math.min(init.day - 1, 28));
  const [lunarHourIdx, setLunarHourIdx] = useState(() => {
    // 无真实小时（空值/档案占位）时默认午时（索引 7，对应小时 12）
    if (!hasRealBirthHour(value)) return 7;
    const h = init.hour;
    // lunarHourLabels = [unknownLabel, '子 00', '丑 02', ..., '亥 22']
    if (h >= 0 && h <= 23) return Math.floor(h / 2) + 1;
    return 0;
  });

  const lunarYear = YEAR_START + lunarYearIdx;

  // Build lunar month list for selected year (12 or 13 if leap month exists)
  const lunarLeapMonth = getLunarLeapMonth(lunarYear);
  const lunarMonthList: string[] = [];
  const lunarMonthMapping: Array<{ month: number; isLeap: boolean }> = [];

  for (let m = 1; m <= 12; m++) {
    lunarMonthList.push(lunarMonthNames[m - 1] ?? `M${m}`);
    lunarMonthMapping.push({ month: m, isLeap: false });
    if (lunarLeapMonth === m) {
      lunarMonthList.push(`${leapPrefix}${lunarMonthNames[m - 1] ?? `M${m}`}`);
      lunarMonthMapping.push({ month: m, isLeap: true });
    }
  }

  const lunarYears = Array.from(
    { length: YEAR_END - YEAR_START + 1 },
    (_, i) => lunarYearUnit ? `${YEAR_START + i}${lunarYearUnit}` : String(YEAR_START + i),
  );

  // Clamp lunar month index when year changes (month count may change 12↔13)
  useEffect(() => {
    if (lunarMonthIdx >= lunarMonthList.length) {
      setLunarMonthIdx(lunarMonthList.length - 1);
    }
  }, [lunarYear]);

  // Dynamic lunar day count
  const lunarMonthInfo = lunarMonthMapping[lunarMonthIdx] ?? { month: 1, isLeap: false };
  const lunarDayCount = lunarMonthInfo.isLeap
    ? getLunarLeapMonthDays(lunarYear)
    : getLunarMonthDays(lunarYear, lunarMonthInfo.month);

  const lunarDays = lunarDayNames.slice(0, lunarDayCount);
  const lunarDayColKey = `lunar-day-${lunarYear}-${lunarMonthIdx}`;

  // Clamp lunar day when month/year changes
  useEffect(() => {
    if (lunarDayIdx >= lunarDayCount) {
      setLunarDayIdx(lunarDayCount - 1);
    }
  }, [lunarDayColKey]);

  // Lunar time labels
  const lunarHourLabels = [
    unknownLabel,
    ...FOUR_PILLARS_HOURS_BASE.map((h, i) => {
      const start = (i * 2) % 24;
      const startStr = String(start).padStart(2, '0');
      return lunarHourUnit ? `${h}${lunarHourUnit} ${startStr}` : `${h} ${startStr}`;
    }),
  ];

  // ── 四柱 ──
  const initBirthTime = `${String(init.year).padStart(4, '0')}-${String(init.month).padStart(2, '0')}-${String(init.day).padStart(2, '0')} ${String(init.hour).padStart(2, '0')}:${String(init.minute).padStart(2, '0')}`;
  const initFP: FourPillarsValue = solarToFourPillarsValue(initBirthTime);
  const [fpValue, setFpValue] = useState<FourPillarsValue>(initFP);
  const fourPillarsPreview = reverseFourPillarsToSolar(fpValue);

  // ── 今天 ──
  const handleToday = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    const h = now.getHours();
    const min = now.getMinutes();
    if (activeTab === 'solar') {
      setSolarYearIdx(y - YEAR_START);
      setSolarMonthIdx(m);
      setSolarDayIdx(d - 1);
      setSolarHourIdx(h + 1);
      setSolarMinIdx(min + 1);
    } else if (activeTab === 'lunar') {
      setLunarYearIdx(y - YEAR_START);
      setLunarMonthIdx(m);
      setLunarDayIdx(Math.min(d - 1, lunarDayCount - 1));
      setLunarHourIdx(Math.floor(h / 2) + 1);
    } else {
      const todayBirthTime = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')} ${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      setFpValue(solarToFourPillarsValue(todayBirthTime));
    }
  };

  // ── 确定 ──
  const handleConfirm = () => {
    let result = '';
    if (activeTab === 'solar') {
      const y = String(YEAR_START + solarYearIdx).padStart(4, '0');
      const m = String(solarMonthIdx + 1).padStart(2, '0');
      const d = String(solarDayIdx + 1).padStart(2, '0');
      const isHourUnknown = solarHourIdx === 0;
      const isMinUnknown  = solarMinIdx === 0;
      const hStr   = isHourUnknown ? unknownLabel : String(solarHourIdx - 1).padStart(2, '0');
      const minStr = isMinUnknown  ? unknownLabel : String(solarMinIdx - 1).padStart(2, '0');
      result = `${y}-${m}-${d} ${hStr}:${minStr}`;
    } else if (activeTab === 'lunar') {
      const y = YEAR_START + lunarYearIdx;
      const info = lunarMonthMapping[lunarMonthIdx] ?? { month: 1, isLeap: false };
      let solar = lunarToSolar(y, info.month, lunarDayIdx + 1, info.isLeap);
      if (!solar) {
        solar = lunarToSolar(y, info.month, Math.min(lunarDayIdx + 1, lunarDayCount), info.isLeap);
      }
      const isHourUnknown = lunarHourIdx === 0;
      const hStr = isHourUnknown ? unknownLabel : String((lunarHourIdx - 1) * 2).padStart(2, '0');
      if (solar) {
        result = `${String(solar.year).padStart(4, '0')}-${String(solar.month).padStart(2, '0')}-${String(solar.day).padStart(2, '0')} ${hStr}:00`;
      } else {
        result = '';
      }
    } else {
      // Four pillars: 反推为标准阳历
      const reversed = reverseFourPillarsToSolar(fpValue);
      result = reversed ?? '';
    }
    onChange(result);
    onClose();
  };

  const TABS: { key: TabType; label: string }[] = [
    { key: 'solar',       label: t('addModal.solar') },
    { key: 'lunar',       label: t('addModal.lunar') },
    { key: 'fourPillars', label: t('addModal.fourPillars') },
  ];

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end"
      style={{ maxWidth: 375, margin: '0 auto' }}
    >
      <div
        className="absolute inset-0 bg-black/60"
        style={{ backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      />
      <div
        className="relative w-full rounded-t-3xl shadow-2xl border-t overflow-hidden"
        style={{
          background: 'var(--bg-primary)',
          maxHeight: '85dvh',
          borderColor: 'var(--border-color)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 顶部拖拽条 */}
        <div
          className="w-10 h-1 rounded-full mx-auto mt-4 mb-0"
          style={{ background: 'var(--text-primary-30)' }}
        />

        {/* 标题栏 */}
        <div className="relative flex items-center justify-center px-5 py-4">
          <div className="flex items-center gap-2">
            <div
              className="w-1 h-4 rounded-full"
              style={{
                background: 'var(--accent-primary)',
                boxShadow: isDark ? '0 0 6px rgba(183,154,197,0.5)' : '0 0 6px rgba(232,160,32,0.4)',
              }}
            />
            <h3
              className="text-base font-semibold"
              style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-primary)' }}
            >
              {title || t('birthTimePicker.title')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="absolute right-4 -top-1 w-8 h-8 flex items-center justify-center rounded-full transition-colors"
          >
            <i className="ri-close-line text-lg" style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>

        {/* Tab 栏 + 今 */}
        <div className="flex items-center px-4 pt-3 pb-2 gap-2"
        >
          <div
            className="flex items-center gap-1 flex-1 rounded-full p-1"
            style={{ background: isDark ? 'var(--panel-dark-25)' : 'var(--accent-primary-10)' }}
          >
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="flex-1 py-1.5 rounded-full text-xs font-medium transition-all"
                style={{
                  background: activeTab === tab.key ? 'var(--accent-primary-25)' : 'transparent',
                  color: activeTab === tab.key ? (isDark ? 'var(--text-primary)' : 'var(--accent-primary)') : 'var(--text-secondary)',
                  border: activeTab === tab.key ? '1px solid var(--accent-primary-40)' : '1px solid transparent',
                  fontFamily: "'Noto Serif TC', serif",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleToday}
            className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
            style={{
              background: isDark ? 'var(--panel-dark-25)' : 'var(--accent-primary-10)',
              color: 'var(--accent-primary)',
              border: '1px solid var(--accent-primary-30)',
              fontFamily: "'Noto Serif TC', serif",
            }}
          >
            {t('birthTimePicker.today')}
          </button>
        </div>

        {/* 列标题 (solar + lunar only) */}
        {activeTab !== 'fourPillars' && (
          <div className="flex px-3 pb-1">
            {activeTab === 'solar' && (
              <>
                <div className="flex-1 text-center text-[0.65rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthTimePicker.year')}</div>
                <div className="flex-1 text-center text-[0.65rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthTimePicker.month')}</div>
                <div className="flex-1 text-center text-[0.65rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthTimePicker.day')}</div>
                <div className="flex-1 text-center text-[0.65rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthTimePicker.hour')}</div>
                <div className="flex-1 text-center text-[0.65rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthTimePicker.minute')}</div>
              </>
            )}
            {activeTab === 'lunar' && (
              <>
                <div className="flex-1 text-center text-[0.65rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthTimePicker.year')}</div>
                <div className="flex-1 text-center text-[0.65rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthTimePicker.month')}</div>
                <div className="flex-1 text-center text-[0.65rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthTimePicker.day')}</div>
                <div className="flex-[1.5] text-center text-[0.65rem]" style={{ fontFamily: "'Noto Serif TC', serif", color: 'var(--text-tertiary)' }}>{t('birthTimePicker.shichen')}</div>
              </>
            )}
          </div>
        )}

        {activeTab !== 'fourPillars' && (
          <div className="mx-4 h-px" style={{ background: 'var(--accent-primary-15)' }} />
        )}

        {/* 滚轮区域 or 四柱选择器 */}
        {activeTab === 'solar' && (
          <div className="flex min-h-0 px-3 py-1">
            <WheelColumn items={solarYears} selectedIndex={solarYearIdx} onSelect={setSolarYearIdx} />
            <WheelColumn items={solarMonths} selectedIndex={solarMonthIdx} onSelect={setSolarMonthIdx} />
            <WheelColumn
              colKey={solarDayColKey}
              items={solarDays}
              selectedIndex={Math.min(solarDayIdx, solarDays.length - 1)}
              onSelect={setSolarDayIdx}
            />
            <WheelColumn items={solarHours} selectedIndex={solarHourIdx} onSelect={setSolarHourIdx} />
            <WheelColumn items={solarMins}  selectedIndex={solarMinIdx}  onSelect={setSolarMinIdx} />
          </div>
        )}

        {activeTab === 'lunar' && (
          <div className="flex min-h-0 px-3 py-1">
            <WheelColumn items={lunarYears} selectedIndex={lunarYearIdx} onSelect={setLunarYearIdx} />
            <WheelColumn
              colKey={`lunar-month-${lunarYear}`}
              items={lunarMonthList}
              selectedIndex={Math.min(lunarMonthIdx, lunarMonthList.length - 1)}
              onSelect={setLunarMonthIdx}
            />
            <WheelColumn
              colKey={lunarDayColKey}
              items={lunarDays}
              selectedIndex={Math.min(lunarDayIdx, lunarDays.length - 1)}
              onSelect={setLunarDayIdx}
            />
            <WheelColumn
              items={lunarHourLabels}
              selectedIndex={lunarHourIdx}
              onSelect={setLunarHourIdx}
              width="flex-[1.5]"
            />
          </div>
        )}

        {activeTab === 'fourPillars' && (
          <FourPillarsTab initial={fpValue} onChange={setFpValue} />
        )}

        {activeTab === 'fourPillars' && (
          <div className="px-5 pt-2 pb-1">
            <div className="rounded-xl border px-3 py-2 flex items-center gap-2"
              style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <i className="ri-calendar-check-line text-sm" style={{ color: 'var(--accent-primary)' }} />
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {fourPillarsPreview ? `对应阳历：${fourPillarsPreview.slice(0, 10)} ${fourPillarsPreview.slice(11, 16)}` : '四柱未完整，暂无法反推'}
              </span>
            </div>
          </div>
        )}

        {/* 确认按钮 + 提示 */}
        <div
          className="px-5 pb-6 pt-2 shrink-0"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}
        >
          <p
            className="text-center mb-2 text-xs"
            style={{
              fontFamily: "'Noto Serif TC', serif",
              color: '#8A7B91',
              fontSize: '8px',
            }}
          >
            {activeTab === 'fourPillars'
              ? t('birthTimePicker.noteFourPillars')
              : t('birthTimePicker.noteTime')}
          </p>
          <button
            disabled={activeTab === 'fourPillars' && !fourPillarsPreview}
            className="w-full py-4 rounded-2xl text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50 disabled:pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, var(--gradient-btn-from), var(--gradient-btn-to))',
              color: 'var(--btn-text)',
              boxShadow: '0 0 16px var(--gradient-btn-glow)',
              fontFamily: "'Noto Serif TC', serif",
            }}
            onClick={handleConfirm}
          >
            {t('birthTimePicker.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ColSelectProps {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  options: Array<{ label: string; value: string | number | null }>;
  selectRef?: React.Ref<HTMLSelectElement>;
}

/** Desktop BirthTimePicker: centered modal with cascading <select> dropdowns (陽曆 tab), plus lunar and four-pillars tabs */
function DesktopBirthTimePicker({ value, onChange, onClose, title }: BirthTimePickerProps) {
  const { t } = useTranslation('profileManage');
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<'solar' | 'lunar' | 'fourPillars'>('solar');

  const lunarMonthNames = t('birthTimePicker.lunarMonths', { returnObjects: true }) as string[];
  const lunarDayNames = t('birthTimePicker.lunarDays', { returnObjects: true }) as string[];
  const leapPrefix = t('birthTimePicker.leapPrefix');
  const lunarYearUnit = t('birthTimePicker.lunarYearUnit');
  const lunarHourUnit = t('birthTimePicker.lunarHourUnit');
  const unknownLabel = t('birthTimePicker.unknown');

  const parseInitial = () => {
    const normalized = value.includes('T') ? value.replace('T', ' ') : value;
    const parts = normalized.split(' ');
    const dateParts = (parts[0] || '1990-01-01').split('-');
    const timeParts = (parts[1] || '00:00').split(':');
    return {
      year: parseInt(dateParts[0]) || 1990,
      month: parseInt(dateParts[1]) || 1,
      day: parseInt(dateParts[2]) || 1,
      hour: parseInt(timeParts[0]) || 0,
      minute: parseInt(timeParts[1]) || 0,
    };
  };
  const init = parseInitial();
  const YEAR_START = 1900;
  const YEAR_END = 2100;

  // Solar
  const [sYear, setSYear] = useState(init.year);
  const [sMonth, setSMonth] = useState(init.month);
  const [sDay, setSDay] = useState(init.day);
  const [sHour, setSHour] = useState<number | null>(() => !hasRealBirthHour(value) ? 12 : (init.hour >= 0 && init.hour <= 23 ? init.hour : null));
  const [sMin, setSMin] = useState<number | null>(init.minute >= 0 && init.minute <= 59 ? init.minute : null);

  const sDayCount = new Date(sYear, sMonth, 0).getDate();
  useEffect(() => { if (sDay > sDayCount) setSDay(sDayCount); }, [sYear, sMonth, sDayCount]);

  // Lunar
  const [lYear, setLYear] = useState(init.year);
  const [lMonthIdx, setLMonthIdx] = useState(init.month - 1);
  const [lDay, setLDay] = useState(Math.min(init.day, 29));
  const [lHour, setLHour] = useState<number | null>(() => !hasRealBirthHour(value) ? 12 : null);

  const lunarLeapMonth = getLunarLeapMonth(lYear);
  const lunarMonthList: string[] = [];
  const lunarMonthMapping: Array<{ month: number; isLeap: boolean }> = [];
  for (let m = 1; m <= 12; m++) {
    lunarMonthList.push(lunarMonthNames[m - 1] ?? `M${m}`);
    lunarMonthMapping.push({ month: m, isLeap: false });
    if (lunarLeapMonth === m) {
      lunarMonthList.push(`${leapPrefix}${lunarMonthNames[m - 1] ?? `M${m}`}`);
      lunarMonthMapping.push({ month: m, isLeap: true });
    }
  }

  const lunarMonthInfo = lunarMonthMapping[lMonthIdx] ?? { month: 1, isLeap: false };
  const lunarDayCount = lunarMonthInfo.isLeap
    ? getLunarLeapMonthDays(lYear)
    : getLunarMonthDays(lYear, lunarMonthInfo.month);

  useEffect(() => { if (lMonthIdx >= lunarMonthList.length) setLMonthIdx(lunarMonthList.length - 1); }, [lYear]);
  useEffect(() => { if (lDay > lunarDayCount) setLDay(lunarDayCount); }, [lYear, lMonthIdx, lunarDayCount]);

  const FOUR_PILLARS_HOURS_BASE = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
  const lunarHourOptions = [
    { label: unknownLabel, value: null },
    ...FOUR_PILLARS_HOURS_BASE.map((h, i) => {
      const start = (i * 2) % 24;
      return { label: lunarHourUnit ? `${h}${lunarHourUnit} ${String(start).padStart(2, '0')}` : `${h} ${String(start).padStart(2, '0')}`, value: (i * 2) % 24 };
    }),
  ];

  // Four pillars
  const [fpValue, setFpValue] = useState<FourPillarsValue>(() => {
    const initBirthTime = `${String(init.year).padStart(4, '0')}-${String(init.month).padStart(2, '0')}-${String(init.day).padStart(2, '0')} ${String(init.hour).padStart(2, '0')}:${String(init.minute).padStart(2, '0')}`;
    return solarToFourPillarsValue(initBirthTime);
  });
  const fourPillarsPreview = reverseFourPillarsToSolar(fpValue);

  const handleToday = () => {
    const now = new Date();
    if (activeTab === 'solar') {
      setSYear(now.getFullYear()); setSMonth(now.getMonth() + 1); setSDay(now.getDate()); setSHour(now.getHours()); setSMin(now.getMinutes());
    } else if (activeTab === 'lunar') {
      setLYear(now.getFullYear()); setLMonthIdx(now.getMonth()); setLDay(Math.min(now.getDate(), lunarDayCount)); setLHour(now.getHours());
    } else {
      const nowY = now.getFullYear();
      const nowM = now.getMonth() + 1;
      const nowD = now.getDate();
      const nowH = now.getHours();
      const nowMin = now.getMinutes();
      const todayBirthTime = `${nowY}-${String(nowM).padStart(2, '0')}-${String(nowD).padStart(2, '0')} ${String(nowH).padStart(2, '0')}:${String(nowMin).padStart(2, '0')}`;
      setFpValue(solarToFourPillarsValue(todayBirthTime));
    }
  };

  const handleConfirm = () => {
    let result = '';
    if (activeTab === 'solar') {
      const y = String(sYear).padStart(4, '0');
      const m = String(sMonth).padStart(2, '0');
      const d = String(sDay).padStart(2, '0');
      const hStr = sHour == null ? unknownLabel : String(sHour).padStart(2, '0');
      const minStr = sMin == null ? unknownLabel : String(sMin).padStart(2, '0');
      result = `${y}-${m}-${d} ${hStr}:${minStr}`;
    } else if (activeTab === 'lunar') {
      const info = lunarMonthMapping[lMonthIdx] ?? { month: 1, isLeap: false };
      let solar = lunarToSolar(lYear, info.month, lDay, info.isLeap);
      if (!solar) {
        solar = lunarToSolar(lYear, info.month, Math.min(lDay, lunarDayCount), info.isLeap);
      }
      const hStr = lHour == null ? unknownLabel : String(lHour).padStart(2, '0');
      if (solar) {
        result = `${String(solar.year).padStart(4, '0')}-${String(solar.month).padStart(2, '0')}-${String(solar.day).padStart(2, '0')} ${hStr}:00`;
      } else {
        result = '';
      }
    } else {
      const reversed = reverseFourPillarsToSolar(fpValue);
      result = reversed ?? '';
    }
    onChange(result);
    onClose();
  };

  const TABS = [
    { key: 'solar' as const, label: t('addModal.solar') },
    { key: 'lunar' as const, label: t('addModal.lunar') },
    { key: 'fourPillars' as const, label: t('addModal.fourPillars') },
  ];

  const selectStyle: React.CSSProperties = {
    background: isDark ? 'rgba(50,30,60,0.95)' : '#fff',
    color: isDark ? '#F0E0E8' : '#1A1A1A',
    border: `1px solid ${isDark ? 'rgba(183,154,197,0.25)' : 'rgba(0,0,0,0.1)'}`,
    borderRadius: 8,
    padding: '7px 28px 7px 10px',
    fontSize: '0.8rem',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    fontFamily: "'Noto Serif TC', serif",
    width: '100%',
  };

  // refs for cascading select open
  const yearRef = useRef<HTMLSelectElement>(null);
  const monthRef = useRef<HTMLSelectElement>(null);
  const dayRef = useRef<HTMLSelectElement>(null);
  const hourRef = useRef<HTMLSelectElement>(null);
  const minRef = useRef<HTMLSelectElement>(null);
  const lunarYearRef = useRef<HTMLSelectElement>(null);
  const lunarMonthRef = useRef<HTMLSelectElement>(null);
  const lunarDayRef = useRef<HTMLSelectElement>(null);
  const lunarHourRef = useRef<HTMLSelectElement>(null);

  const openSelect = useCallback((ref: React.RefObject<HTMLSelectElement | null>) => {
    setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const sel = el as HTMLSelectElement & { showPicker?: () => void };
      if (typeof sel.showPicker === 'function') {
        try { sel.showPicker(); } catch { el.focus(); }
      } else {
        el.focus();
      }
    }, 60);
  }, []);

  const ColSelect = ({ label, value: val, onChange: onCh, options: opts, selectRef }: ColSelectProps) => (
    <div className="flex flex-col gap-1 flex-1">
      <label className="text-[0.6rem] text-center" style={{ color: isDark ? '#8A7B91' : '#888', fontFamily: "'Noto Serif TC', serif" }}>{label}</label>
      <div className="relative">
        <select
          ref={selectRef}
          value={String(val)}
          onChange={e => onCh(e.target.value)}
          style={selectStyle}
        >
          {opts.map((o, i) => <option key={i} value={String(o.value)}>{o.label}</option>)}
        </select>
        <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-xs" style={{ color: isDark ? '#8A7B91' : '#888' }} />
      </div>
    </div>
  );

  const years = Array.from({ length: YEAR_END - YEAR_START + 1 }, (_, i) => ({ label: String(YEAR_START + i), value: YEAR_START + i }));
  const months = Array.from({ length: 12 }, (_, i) => ({ label: String(i + 1).padStart(2, '0'), value: i + 1 }));
  const solarDays = Array.from({ length: sDayCount }, (_, i) => ({ label: String(i + 1).padStart(2, '0'), value: i + 1 }));
  const hours = [{ label: unknownLabel, value: 'null' }, ...Array.from({ length: 24 }, (_, i) => ({ label: String(i).padStart(2, '0'), value: i }))];
  const minutes = [{ label: unknownLabel, value: 'null' }, ...Array.from({ length: 60 }, (_, i) => ({ label: String(i).padStart(2, '0'), value: i }))];

  const lunarYears = Array.from({ length: YEAR_END - YEAR_START + 1 }, (_, i) => ({ label: lunarYearUnit ? `${YEAR_START + i}${lunarYearUnit}` : String(YEAR_START + i), value: YEAR_START + i }));
  const lunarMonths = lunarMonthList.map((l, i) => ({ label: l, value: i }));
  const lunarDays = lunarDayNames.slice(0, lunarDayCount).map((l, i) => ({ label: l, value: i + 1 }));

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/55" style={{ backdropFilter: 'blur(5px)' }} onClick={onClose} />
      <div
        className="relative rounded-2xl border shadow-2xl"
        style={{
          background: isDark ? '#2A1A2F' : '#fff',
          borderColor: isDark ? 'rgba(183,154,197,0.3)' : 'rgba(0,0,0,0.1)',
          width: 520,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: isDark ? 'rgba(183,154,197,0.15)' : 'rgba(0,0,0,0.08)' }}>
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full" style={{ background: 'var(--accent-primary)', boxShadow: isDark ? '0 0 6px rgba(183,154,197,0.5)' : '0 0 6px rgba(232,160,32,0.4)' }} />
            <h3 className="text-sm font-semibold" style={{ fontFamily: "'Noto Serif TC', serif", color: isDark ? '#F0E0E8' : '#1A1A1A' }}>{title || t('birthTimePicker.title')}</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full" style={{ background: isDark ? 'rgba(183,154,197,0.12)' : 'rgba(0,0,0,0.06)' }}>
            <i className="ri-close-line text-sm" style={{ color: isDark ? '#8A7B91' : '#666' }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* Tab + 今 */}
          <div className="flex items-center gap-2 mb-5">
            <div className="flex items-center gap-1 flex-1 rounded-full p-1" style={{ background: isDark ? 'var(--panel-dark-25)' : 'var(--accent-primary-10)' }}>
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className="flex-1 py-1.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: activeTab === tab.key ? 'var(--accent-primary-25)' : 'transparent',
                    color: activeTab === tab.key ? (isDark ? 'var(--text-primary)' : 'var(--accent-primary)') : 'var(--text-secondary)',
                    border: activeTab === tab.key ? '1px solid var(--accent-primary-40)' : '1px solid transparent',
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button
              onClick={handleToday}
              className="px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ background: isDark ? 'var(--panel-dark-25)' : 'var(--accent-primary-10)', color: 'var(--accent-primary)', border: '1px solid var(--accent-primary-30)', fontFamily: "'Noto Serif TC', serif" }}
            >
              {t('birthTimePicker.today')}
            </button>
          </div>

          {/* Solar select */}
          {activeTab === 'solar' && (
            <div className="flex gap-2">
              <ColSelect
                label={t('birthTimePicker.year')}
                value={sYear}
                onChange={v => { setSYear(Number(v)); openSelect(monthRef); }}
                options={years}
                selectRef={yearRef}
              />
              <ColSelect
                label={t('birthTimePicker.month')}
                value={sMonth}
                onChange={v => { setSMonth(Number(v)); openSelect(dayRef); }}
                options={months}
                selectRef={monthRef}
              />
              <ColSelect
                label={t('birthTimePicker.day')}
                value={sDay}
                onChange={v => {
                  setSDay(Number(v));
                  if (sHour == null) setSHour(12);
                  openSelect(hourRef);
                }}
                options={solarDays}
                selectRef={dayRef}
              />
              <ColSelect
                label={t('birthTimePicker.hour')}
                value={sHour == null ? 'null' : sHour}
                onChange={v => { setSHour(v === 'null' ? null : Number(v)); openSelect(minRef); }}
                options={hours}
                selectRef={hourRef}
              />
              <ColSelect
                label={t('birthTimePicker.minute')}
                value={sMin == null ? 'null' : sMin}
                onChange={v => setSMin(v === 'null' ? null : Number(v))}
                options={minutes}
                selectRef={minRef}
              />
            </div>
          )}

          {/* Lunar select */}
          {activeTab === 'lunar' && (
            <div className="flex gap-2">
              <ColSelect
                label={t('birthTimePicker.year')}
                value={lYear}
                onChange={v => { setLYear(Number(v)); openSelect(lunarMonthRef); }}
                options={lunarYears}
                selectRef={lunarYearRef}
              />
              <ColSelect
                label={t('birthTimePicker.month')}
                value={lMonthIdx}
                onChange={v => { setLMonthIdx(Number(v)); openSelect(lunarDayRef); }}
                options={lunarMonths}
                selectRef={lunarMonthRef}
              />
              <ColSelect
                label={t('birthTimePicker.day')}
                value={lDay}
                onChange={v => { setLDay(Number(v)); openSelect(lunarHourRef); }}
                options={lunarDays}
                selectRef={lunarDayRef}
              />
              <ColSelect
                label={t('birthTimePicker.shichen')}
                value={lHour == null ? 'null' : lHour}
                onChange={v => setLHour(v === 'null' ? null : Number(v))}
                options={lunarHourOptions.map(o => ({ label: o.label, value: o.value == null ? 'null' : o.value }))}
                selectRef={lunarHourRef}
              />
            </div>
          )}

          {/* Four pillars */}
          {activeTab === 'fourPillars' && (
            <FourPillarsTab initial={fpValue} onChange={setFpValue} />
          )}

          {activeTab === 'fourPillars' && (
            <div className="px-1 pt-2 pb-1">
              <div className="rounded-xl border px-3 py-2 flex items-center gap-2"
                style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                <i className="ri-calendar-check-line text-sm" style={{ color: 'var(--accent-primary)' }} />
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {fourPillarsPreview ? `对应阳历：${fourPillarsPreview.slice(0, 10)} ${fourPillarsPreview.slice(11, 16)}` : '四柱未完整，暂无法反推'}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t" style={{ borderColor: isDark ? 'rgba(183,154,197,0.15)' : 'rgba(0,0,0,0.08)' }}>
          <p
            className="text-center mb-2 text-xs"
            style={{
              fontFamily: "'Noto Serif TC', serif",
              color: '#8A7B91',
              fontSize: '10px',
            }}
          >
            {activeTab === 'fourPillars'
              ? t('birthTimePicker.noteFourPillars')
              : t('birthTimePicker.noteTime')}
          </p>
          <button
            disabled={activeTab === 'fourPillars' && !fourPillarsPreview}
            onClick={handleConfirm}
            className="w-full py-2.5 rounded-xl text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:pointer-events-none"
            style={{ background: 'linear-gradient(135deg, var(--gradient-btn-from), var(--gradient-btn-to))', color: 'var(--btn-text)', fontFamily: "'Noto Serif TC', serif" }}
          >
            {t('birthTimePicker.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BirthTimePicker(props: BirthTimePickerProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  if (isDesktop) return <DesktopBirthTimePicker {...props} />;
  return <MobileBirthTimePicker {...props} />;
}